import type { JSONContent } from "@tiptap/core";
import type { PostgrestError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Database, Json } from "@/lib/database.types";
import { getPrimaryBookExportArtifact } from "@/lib/book-assembly/export-report";
import {
  createDefaultBookExportConfig,
  isBookExportCurrentStage,
} from "@/lib/book-assembly/export-contract";
import { convertPlainTextToHtml } from "@/lib/book-assembly/export-text";
import type {
  BookAssemblyLaunchState,
  BookAssemblyResult,
  BookAssemblySourceChapter,
  BookAssemblySourceSideStory,
  BookAssemblySourceView,
  BookExportArtifact,
  BookExportConfig,
  BookExportJob,
  BookExportReport,
  BookExportProjectOverrides,
  RenderedBookExportTarget,
  BookExportSettings,
  CanonicalBookExportManifest,
  SideStoryPlacementSuggestionSet,
} from "@/features/book-assembly/book-assembly.types";
import {
  buildBookAssemblyLaunchState,
  buildPlacementSuggestionPayload,
  buildStartBookExportPayload,
  mapBookAssemblySource,
} from "@/features/book-assembly/bookAssemblyMappers";
import { normalizeStoryDocument } from "@/features/story/mappers/storyMappers";
import type { SubscriptionTier } from "@/types/projects";
import { chapterStatusLabels } from "@/types/story";

type StoryRow = Database["public"]["Tables"]["stories"]["Row"];
type ChapterRow = Database["public"]["Tables"]["chapters"]["Row"];
type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
type BookAssemblyJobRow = Database["public"]["Tables"]["book_assemblies"]["Row"];

type BookAssemblyErrorCode =
  | "access-denied"
  | "not-found"
  | "placement-failed"
  | "source-not-found"
  | "tier-locked"
  | "insufficient-credits"
  | "invalid-order"
  | "empty-content"
  | "render-failed"
  | "generation-failed"
  | "upload-failed"
  | "job-not-found"
  | "retry-not-allowed"
  | "processing-failed"
  | "unknown";

type BookAssemblyErrorDetails = {
  option?: string | null;
  currentBalance?: number | null;
  requiredTier?: SubscriptionTier | null;
  recommendedTier?: SubscriptionTier | null;
  setting?: string | null;
  tier?: SubscriptionTier | null;
};

type StartExportResponse = {
  currentStage: string | null;
  jobId: string;
  retryCount: number;
  status: string;
};

type StartBookExportPayload = {
  action: "start_export";
  config: BookExportConfig;
  projectId: string;
  projectOverrides?: BookExportProjectOverrides;
};

export class BookAssemblyActionError extends Error {
  constructor(
    public readonly code: BookAssemblyErrorCode,
    message: string,
    public readonly details: BookAssemblyErrorDetails = {},
  ) {
    super(message);
    this.name = "BookAssemblyActionError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function mapPostgrestError(error: PostgrestError) {
  if (error.code === "PGRST116") {
    return new BookAssemblyActionError("not-found", error.message);
  }

  if (error.code === "42501" || error.message.toLowerCase().includes("row-level security")) {
    return new BookAssemblyActionError("access-denied", error.message);
  }

  return new BookAssemblyActionError("unknown", error.message);
}

function ensureRecord<T>(record: T | null, message = "That project is unavailable.") {
  if (!record) {
    throw new BookAssemblyActionError("not-found", message);
  }

  return record;
}

function coerceTier(value: unknown): SubscriptionTier {
  return value === "crafter" || value === "author" ? value : "free";
}

function coerceJsonContent(value: unknown) {
  return normalizeStoryDocument(value) as JSONContent;
}

function coerceString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function coerceStringArray(value: unknown) {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function coerceBookExportConfig(value: Json): BookExportConfig {
  if (!isRecord(value)) {
    return createDefaultBookExportConfig();
  }

  return createDefaultBookExportConfig({
    endMatterMessageSource:
      value.endMatterMessageSource === "author_note" || value.endMatterMessageSource === "none"
        ? value.endMatterMessageSource
        : "special_thanks",
    exportMode:
      value.exportMode === "side_stories_only" || value.exportMode === "full_compendium"
        ? value.exportMode
        : "story_only",
    frontMatterMessageSource:
      value.frontMatterMessageSource === "dedication"
      || value.frontMatterMessageSource === "none"
        ? value.frontMatterMessageSource
        : "author_note",
    includeCharacterAppendix: value.includeCharacterAppendix === true,
    includeCover: value.includeCover !== false,
    includeIllustrations: value.includeIllustrations === true,
    includeLoreAppendix: value.includeLoreAppendix === true,
    includeTimelineAppendix: value.includeTimelineAppendix === true,
    includeTitlePage: value.includeTitlePage !== false,
    includeToc: value.includeToc !== false,
    mirroredMargins: value.mirroredMargins === true,
    pageSize:
      value.pageSize === "five_by_eight"
      || value.pageSize === "six_by_nine"
      || value.pageSize === "letter"
      || value.pageSize === "a4"
        ? value.pageSize
        : "a5",
    preset:
      value.preset === "book_cream"
      || value.preset === "parchment"
      || value.preset === "slate_dark"
      || value.preset === "minimal_modern"
        ? value.preset
        : "classic_white",
    printMode: value.printMode === true,
    selectedSideStoryIds: coerceStringArray(value.selectedSideStoryIds),
    showPageNumbers: value.showPageNumbers !== false,
    startChaptersOnRightPage: value.startChaptersOnRightPage === true,
    target: value.target === "docx" || value.target === "both" ? value.target : "pdf",
  });
}

function mapBookExportArtifact(value: unknown): BookExportArtifact | null {
  if (!isRecord(value)) {
    return null;
  }

  const target = value.target === "docx" ? "docx" : value.target === "pdf" ? "pdf" : null;
  const fileName = coerceString(value.fileName);
  const storagePath = coerceString(value.storagePath);
  const mimeType = coerceString(value.mimeType);
  const byteSize = typeof value.byteSize === "number" ? value.byteSize : 0;
  const createdAt = coerceString(value.createdAt);

  if (!target || !fileName || !storagePath || !mimeType || !createdAt) {
    return null;
  }

  return {
    byteSize,
    createdAt,
    fileName,
    mimeType,
    storagePath,
    target,
  };
}

function mapBookExportReport(value: unknown): BookExportReport | null {
  if (!isRecord(value)) {
    return null;
  }

  return {
    entries: Array.isArray(value.entries)
      ? value.entries.filter(isRecord).map((entry) => ({
          code: coerceString(entry.code) ?? "unknown",
          kind:
            entry.kind === "warning" || entry.kind === "omission" ? entry.kind : "fallback",
          message: coerceString(entry.message) ?? "",
          sectionKey: coerceString(entry.sectionKey) ?? "main_story",
          sourceId: coerceString(entry.sourceId),
          sourceKind:
            entry.sourceKind === "project"
            || entry.sourceKind === "chapter"
            || entry.sourceKind === "side_story"
            || entry.sourceKind === "entity_card"
            || entry.sourceKind === "lore_entry"
            || entry.sourceKind === "timeline_event"
              ? entry.sourceKind
              : "asset",
        }))
      : [],
    generatedAt: coerceString(value.generatedAt) ?? new Date().toISOString(),
    generatedSections: coerceStringArray(value.generatedSections) as BookExportReport["generatedSections"],
  };
}

function mapManifestSnapshot(value: unknown): CanonicalBookExportManifest | null {
  return isRecord(value) ? (value as unknown as CanonicalBookExportManifest) : null;
}

function mapBookAssemblyJobRow(row: BookAssemblyJobRow): BookExportJob {
  return {
    artifacts: Array.isArray(row.artifact_manifest)
      ? row.artifact_manifest
          .map(mapBookExportArtifact)
          .filter((artifact): artifact is BookExportArtifact => artifact !== null)
      : [],
    completedAt: row.completed_at,
    config: coerceBookExportConfig(row.config),
    currentStage: isBookExportCurrentStage(row.current_stage) ? row.current_stage : null,
    failureCode: row.failure_code,
    failureMessage: row.failure_message,
    id: row.id,
    leaseExpiresAt: row.lease_expires_at,
    manifestSnapshot: mapManifestSnapshot(row.manifest_snapshot),
    projectId: row.project_id,
    report: mapBookExportReport(row.report_payload),
    retryCount: row.retry_count,
    status: row.status,
    updatedAt: row.updated_at,
  };
}

export function getBookAssemblyDownloadExpiryDate(result: BookAssemblyResult) {
  const generatedAt = Date.parse(result.generatedAt);
  if (Number.isNaN(generatedAt)) {
    return null;
  }

  return new Date(generatedAt + result.expiresInSeconds * 1000);
}

export function hasBookAssemblyDownloadExpired(
  result: BookAssemblyResult,
  now = Date.now(),
) {
  const expiresAt = getBookAssemblyDownloadExpiryDate(result);
  return expiresAt ? expiresAt.getTime() <= now : false;
}

async function getCurrentUserId() {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new BookAssemblyActionError("access-denied", "No authenticated user available.");
  }

  return user.id;
}

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new BookAssemblyActionError("access-denied", "PROJECT_ACCESS_DENIED");
  }

  return session.access_token;
}

async function getSignedCoverPreviewUrl(path: string) {
  const result = await supabase.storage.from("exports").createSignedUrl(path, 60 * 30);

  if (result.error || !result.data?.signedUrl) {
    return null;
  }

  return result.data.signedUrl;
}

async function readFunctionError(error: unknown) {
  if (typeof error === "object" && error && "context" in error) {
    const response = (error as { context?: Response }).context;
    if (response) {
      const text = await response.text().catch(() => "");

      if (text) {
        try {
          return JSON.parse(text) as Record<string, unknown>;
        } catch {
          return text.trim();
        }
      }
    }
  }

  return error instanceof Error ? error.message : "BOOK_ASSEMBLY_FAILED";
}

async function normalizePlacementError(error: unknown) {
  if (error instanceof BookAssemblyActionError) {
    return error;
  }

  const payload = await readFunctionError(error);
  if (typeof payload === "string") {
    switch (payload) {
      case "PROJECT_ACCESS_DENIED":
        return new BookAssemblyActionError("access-denied", payload);
      case "BOOK_ASSEMBLY_PLACEMENT_SOURCE_NOT_FOUND":
        return new BookAssemblyActionError("source-not-found", payload);
      default:
        return new BookAssemblyActionError("placement-failed", payload);
    }
  }

  const code = typeof payload.code === "string" ? payload.code : "BOOK_ASSEMBLY_PLACEMENT_FAILED";
  switch (code) {
    case "PROJECT_ACCESS_DENIED":
      return new BookAssemblyActionError("access-denied", code);
    case "TIER_LOCKED":
      return new BookAssemblyActionError("tier-locked", code, {
        currentBalance:
          typeof payload.currentBalance === "number" ? payload.currentBalance : null,
        recommendedTier:
          payload.recommendedTier === "crafter" || payload.recommendedTier === "author"
            ? payload.recommendedTier
            : null,
      });
    case "INSUFFICIENT_CREDITS":
      return new BookAssemblyActionError("insufficient-credits", code, {
        currentBalance:
          typeof payload.currentBalance === "number" ? payload.currentBalance : null,
      });
    case "BOOK_ASSEMBLY_PLACEMENT_SOURCE_NOT_FOUND":
      return new BookAssemblyActionError("source-not-found", code);
    default:
      return new BookAssemblyActionError("placement-failed", code);
  }
}

async function normalizeAssemblyError(error: unknown) {
  if (error instanceof BookAssemblyActionError) {
    return error;
  }

  const payload = await readFunctionError(error);
  if (typeof payload === "string") {
    switch (payload) {
      case "PROJECT_ACCESS_DENIED":
        return new BookAssemblyActionError("access-denied", payload);
      case "BOOK_ASSEMBLY_INVALID_ORDER":
      case "BOOK_EXPORT_INVALID_CONFIG":
        return new BookAssemblyActionError("invalid-order", payload);
      case "BOOK_ASSEMBLY_EMPTY_CONTENT":
        return new BookAssemblyActionError("empty-content", payload);
      case "BOOK_ASSEMBLY_RENDER_FAILED":
      case "BOOK_EXPORT_RENDER_FAILED":
        return new BookAssemblyActionError("render-failed", payload);
      case "BOOK_EXPORT_JOB_NOT_FOUND":
        return new BookAssemblyActionError("job-not-found", payload);
      case "BOOK_EXPORT_RETRY_NOT_ALLOWED":
        return new BookAssemblyActionError("retry-not-allowed", payload);
      case "BOOK_EXPORT_TIER_LOCKED":
        return new BookAssemblyActionError("tier-locked", payload);
      case "BOOK_EXPORT_UPLOAD_FAILED":
        return new BookAssemblyActionError("upload-failed", payload);
      case "BOOK_EXPORT_PROCESSING_FAILED":
        return new BookAssemblyActionError("processing-failed", payload);
      default:
        return new BookAssemblyActionError("generation-failed", payload);
    }
  }

  if (isRecord(payload)) {
    const code = typeof payload.code === "string" ? payload.code : "BOOK_ASSEMBLY_FAILED";
    const details = {
      currentBalance:
        typeof payload.currentBalance === "number" ? payload.currentBalance : null,
      option: coerceString(payload.option),
      recommendedTier:
        payload.recommendedTier === "crafter" || payload.recommendedTier === "author"
          ? payload.recommendedTier
          : payload.recommendedTier === "free"
            ? "free"
            : null,
      requiredTier:
        payload.requiredTier === "crafter" || payload.requiredTier === "author"
          ? payload.requiredTier
          : payload.requiredTier === "free"
            ? "free"
            : null,
      setting: coerceString(payload.setting),
      tier:
        payload.tier === "crafter" || payload.tier === "author"
          ? payload.tier
          : payload.tier === "free"
            ? "free"
            : null,
    } satisfies BookAssemblyErrorDetails;

    switch (code) {
      case "PROJECT_ACCESS_DENIED":
        return new BookAssemblyActionError("access-denied", code, details);
      case "BOOK_EXPORT_INVALID_CONFIG":
      case "BOOK_ASSEMBLY_INVALID_ORDER":
        return new BookAssemblyActionError("invalid-order", code, details);
      case "BOOK_EXPORT_TIER_LOCKED":
        return new BookAssemblyActionError("tier-locked", code, details);
      case "BOOK_EXPORT_JOB_NOT_FOUND":
        return new BookAssemblyActionError("job-not-found", code, details);
      case "BOOK_EXPORT_RETRY_NOT_ALLOWED":
        return new BookAssemblyActionError("retry-not-allowed", code, details);
      case "BOOK_EXPORT_RENDER_FAILED":
      case "BOOK_ASSEMBLY_RENDER_FAILED":
        return new BookAssemblyActionError("render-failed", code, details);
      case "BOOK_EXPORT_UPLOAD_FAILED":
        return new BookAssemblyActionError("upload-failed", code, details);
      case "BOOK_EXPORT_PROCESSING_FAILED":
        return new BookAssemblyActionError("processing-failed", code, details);
      default:
        return new BookAssemblyActionError("generation-failed", code, details);
    }
  }

  return new BookAssemblyActionError("generation-failed", "BOOK_ASSEMBLY_FAILED");
}

export function formatBookAssemblyErrorMessage(error: unknown) {
  const resolved = error instanceof BookAssemblyActionError
    ? error
    : new BookAssemblyActionError("unknown", "BOOK_ASSEMBLY_FAILED");

  switch (resolved.code) {
    case "access-denied":
      return "bookAssembly.errors.accessDenied";
    case "not-found":
      return "bookAssembly.errors.notFound";
    case "tier-locked":
      return "bookAssembly.errors.tierLocked";
    case "insufficient-credits":
      return "bookAssembly.errors.insufficientCredits";
    case "invalid-order":
      return "bookAssembly.errors.invalidOrder";
    case "empty-content":
      return "bookAssembly.errors.emptyContent";
    case "render-failed":
      return "bookAssembly.errors.renderFailed";
    case "source-not-found":
      return "bookAssembly.errors.sourceNotFound";
    case "upload-failed":
      return "bookAssembly.errors.uploadFailed";
    case "placement-failed":
      return "bookAssembly.errors.placementFailed";
    case "job-not-found":
      return "bookAssembly.errors.notFound";
    case "retry-not-allowed":
      return "bookAssembly.errors.retryNotAllowed";
    case "processing-failed":
      return "bookAssembly.errors.processingFailed";
    default:
      return "bookAssembly.errors.generationFailed";
  }
}

function mapBookAssemblyFailureCodeToMessageKey(
  code: string | null | undefined,
) {
  switch (code) {
    case "PROJECT_ACCESS_DENIED":
      return "bookAssembly.errors.accessDenied";
    case "BOOK_EXPORT_INVALID_CONFIG":
    case "BOOK_ASSEMBLY_INVALID_ORDER":
      return "bookAssembly.errors.invalidOrder";
    case "BOOK_ASSEMBLY_EMPTY_CONTENT":
      return "bookAssembly.errors.emptyContent";
    case "BOOK_EXPORT_RENDER_FAILED":
    case "BOOK_ASSEMBLY_RENDER_FAILED":
      return "bookAssembly.errors.renderFailed";
    case "BOOK_EXPORT_UPLOAD_FAILED":
      return "bookAssembly.errors.uploadFailed";
    case "BOOK_EXPORT_JOB_NOT_FOUND":
      return "bookAssembly.errors.notFound";
    case "BOOK_EXPORT_RETRY_NOT_ALLOWED":
      return "bookAssembly.errors.retryNotAllowed";
    case "BOOK_EXPORT_TIER_LOCKED":
      return "bookAssembly.errors.tierLocked";
    case "BOOK_EXPORT_STALE":
    case "BOOK_EXPORT_PROCESSING_FAILED":
      return "bookAssembly.errors.processingFailed";
    default:
      return "bookAssembly.errors.generationFailed";
  }
}

export function getBookAssemblyJobFailureMessageKey(
  job: Pick<BookExportJob, "failureCode" | "failureMessage"> | null | undefined,
) {
  return mapBookAssemblyFailureCodeToMessageKey(job?.failureCode ?? job?.failureMessage);
}

export async function loadBookAssemblySource(projectId: string): Promise<BookAssemblySourceView> {
  const [projectResult, storiesResult, subscriptionResult, profileResult] = await Promise.all([
    supabase
      .from("projects")
      .select("*")
      .eq("id", projectId)
      .maybeSingle(),
    supabase
      .from("stories")
      .select("*")
      .eq("project_id", projectId),
    supabase
      .from("subscriptions")
      .select("tier")
      .maybeSingle(),
    supabase
      .from("profiles")
      .select("*")
      .maybeSingle(),
  ]);

  if (projectResult.error) {
    throw mapPostgrestError(projectResult.error);
  }

  if (storiesResult.error) {
    throw mapPostgrestError(storiesResult.error);
  }

  if (subscriptionResult.error) {
    throw mapPostgrestError(subscriptionResult.error);
  }

  if (profileResult.error) {
    throw mapPostgrestError(profileResult.error);
  }

  const project = ensureRecord(projectResult.data as ProjectRow | null);
  const profile = profileResult.data as ProfileRow | null;
  const stories = ((storiesResult.data as StoryRow[] | null) ?? []).slice();
  const tier = coerceTier(subscriptionResult.data?.tier);
  const mainStory = stories.find((story) => story.type === "main") ?? null;
  const sideStories = stories
    .filter((story) => story.type === "side")
    .sort(
      (left, right) =>
        new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime(),
    );

  let chapters: BookAssemblySourceChapter[] = [];
  if (mainStory?.mode === "chapters") {
    const chaptersResult = await supabase
      .from("chapters")
      .select("*")
      .eq("story_id", mainStory.id)
      .order("sort_order", { ascending: true });

    if (chaptersResult.error) {
      throw mapPostgrestError(chaptersResult.error);
    }

    chapters = ((chaptersResult.data as ChapterRow[] | null) ?? []).map((chapter) => ({
      body: coerceJsonContent(chapter.body),
      id: chapter.id,
      imagePath: chapter.image_path,
      isClosing: chapter.is_closing,
      isOpening: chapter.is_opening,
      sortOrder: chapter.sort_order,
      status: chapter.status,
      statusLabel: chapterStatusLabels[chapter.status],
      title: chapter.title,
      updatedAt: chapter.updated_at,
    }));
  }

  const mappedSideStories: BookAssemblySourceSideStory[] = sideStories.map((story) => ({
    attachmentPaths: Array.isArray(story.image_paths)
      ? story.image_paths.filter((value): value is string => typeof value === "string")
      : [],
    body: coerceJsonContent(story.body),
    id: story.id,
    summary: story.summary ?? "",
    title: story.title,
    updatedAt: story.updated_at,
  }));

  return mapBookAssemblySource({
    authorName: [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() || null,
    authorNoteHtml: project.author_note_html,
    bookSeriesName: project.book_series_name,
    bookSubtitle: project.book_subtitle,
    bookVolumeLabel: project.book_volume_label,
    chapters,
    coverImagePath: project.cover_image_path,
    dedicationHtml: project.dedication_html,
    mainStoryId: mainStory?.id ?? null,
    mainStoryMode: mainStory?.mode ?? null,
    projectId: project.id,
    projectTitle: project.title,
    sideStories: mappedSideStories,
    specialThanksHtml: project.special_thanks_html,
    tier,
  });
}

export async function saveBookAssemblyProjectMetadata(input: {
  projectId: string;
  settings: Pick<
    BookExportSettings,
    "authorNote" | "bookSubtitle" | "dedication" | "seriesName" | "specialThanks" | "volumeLabel"
  >;
}) {
  const result = await supabase
    .from("projects")
    .update({
      author_note_html: convertPlainTextToHtml(input.settings.authorNote),
      book_series_name: input.settings.seriesName.trim() || null,
      book_subtitle: input.settings.bookSubtitle.trim() || null,
      book_volume_label: input.settings.volumeLabel.trim() || null,
      dedication_html: convertPlainTextToHtml(input.settings.dedication),
      special_thanks_html: convertPlainTextToHtml(input.settings.specialThanks),
    })
    .eq("id", input.projectId)
    .select(`
      author_note_html,
      book_series_name,
      book_subtitle,
      book_volume_label,
      dedication_html,
      special_thanks_html
    `)
    .single();

  if (result.error) {
    throw mapPostgrestError(result.error);
  }

  return result.data;
}

export async function loadBookAssemblyJobs(projectId: string): Promise<BookExportJob[]> {
  const result = await supabase
    .from("book_assemblies")
    .select("*")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });

  if (result.error) {
    throw mapPostgrestError(result.error);
  }

  return (result.data ?? []).map((row) => mapBookAssemblyJobRow(row as BookAssemblyJobRow));
}

export async function loadBookAssemblyJob(jobId: string): Promise<BookExportJob> {
  const result = await supabase
    .from("book_assemblies")
    .select("*")
    .eq("id", jobId)
    .single();

  if (result.error) {
    throw mapPostgrestError(result.error);
  }

  return mapBookAssemblyJobRow(result.data as BookAssemblyJobRow);
}

export async function loadBookAssemblyLaunchState(
  projectId: string,
): Promise<BookAssemblyLaunchState> {
  const [source, jobs] = await Promise.all([
    loadBookAssemblySource(projectId),
    loadBookAssemblyJobs(projectId).catch(() => []),
  ]);

  return buildBookAssemblyLaunchState({
    ...source,
    jobs,
  });
}

export async function uploadTemporaryAssemblyCover(input: {
  file: File;
  projectId: string;
  sessionId: string;
}) {
  try {
    const userId = await getCurrentUserId();
    const extension = input.file.name.split(".").pop()?.toLowerCase() ?? "png";
    const fileName = `cover.${extension.replace(/[^a-z0-9]+/g, "") || "png"}`;
    const path = `${userId}/${input.projectId}/assembly-inputs/${input.sessionId}/${fileName}`;
    const uploadResult = await supabase.storage
      .from("exports")
      .upload(path, input.file, {
        cacheControl: "3600",
        upsert: true,
      });

    if (uploadResult.error) {
      throw uploadResult.error;
    }

    return {
      fileName: input.file.name,
      path,
      previewUrl: await getSignedCoverPreviewUrl(path),
    };
  } catch (error) {
    if (error && typeof error === "object" && "message" in error) {
      throw new BookAssemblyActionError("upload-failed", String(error.message));
    }

    throw new BookAssemblyActionError("upload-failed", "BOOK_ASSEMBLY_UPLOAD_FAILED");
  }
}

export async function deleteTemporaryAssemblyCover(path: string | null | undefined) {
  if (!path) {
    return;
  }

  await supabase.storage.from("exports").remove([path]);
}

export async function requestSideStoryPlacementSuggestions(input: {
  draft: Parameters<typeof buildPlacementSuggestionPayload>[0];
}) {
  const payload = buildPlacementSuggestionPayload(input.draft);
  const { data, error } = await supabase.functions.invoke<SideStoryPlacementSuggestionSet>(
    "suggest-side-story-placement",
    {
      body: payload,
    },
  );

  if (error) {
    throw await normalizePlacementError(error);
  }

  if (!data || !Array.isArray(data.suggestions)) {
    throw new BookAssemblyActionError("placement-failed", "BOOK_ASSEMBLY_PLACEMENT_FAILED");
  }

  return data;
}

export async function createSignedBookExportUrl(
  storagePath: string,
  expiresInSeconds = 60 * 30,
) {
  const result = await supabase.storage.from("exports").createSignedUrl(storagePath, expiresInSeconds);

  if (result.error || !result.data?.signedUrl) {
    throw new BookAssemblyActionError("upload-failed", "BOOK_EXPORT_UPLOAD_FAILED");
  }

  return {
    expiresInSeconds,
    signedUrl: result.data.signedUrl,
  };
}

export async function createSignedBookExportArtifactDownload(
  artifact: Pick<BookExportArtifact, "fileName" | "storagePath" | "target">,
  expiresInSeconds = 60 * 30,
) {
  const signed = await createSignedBookExportUrl(artifact.storagePath, expiresInSeconds);

  return {
    downloadUrl: signed.signedUrl,
    expiresInSeconds: signed.expiresInSeconds,
    fileName: artifact.fileName,
    target: artifact.target,
  } satisfies {
    downloadUrl: string;
    expiresInSeconds: number;
    fileName: string;
    target: RenderedBookExportTarget;
  };
}

export async function createSignedBookExportArtifactDownloads(
  artifacts: BookExportArtifact[],
  expiresInSeconds = 60 * 30,
) {
  return Promise.all(
    artifacts.map((artifact) => createSignedBookExportArtifactDownload(artifact, expiresInSeconds)),
  );
}

export async function startBookExport(input: {
  draft: Parameters<typeof buildStartBookExportPayload>[1];
  source: Parameters<typeof buildStartBookExportPayload>[0];
}) {
  const accessToken = await getAccessToken();
  const body: StartBookExportPayload = buildStartBookExportPayload(input.source, input.draft);
  const { data, error } = await supabase.functions.invoke<StartExportResponse>("assemble-book", {
    body,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (error) {
    throw await normalizeAssemblyError(error);
  }

  if (!data?.jobId) {
    throw new BookAssemblyActionError("generation-failed", "BOOK_EXPORT_PROCESSING_FAILED");
  }

  return data;
}

export async function retryBookExport(jobId: string) {
  const accessToken = await getAccessToken();
  const { data, error } = await supabase.functions.invoke<StartExportResponse>("assemble-book", {
    body: {
      action: "retry_export",
      jobId,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (error) {
    throw await normalizeAssemblyError(error);
  }

  if (!data?.jobId) {
    throw new BookAssemblyActionError("retry-not-allowed", "BOOK_EXPORT_RETRY_NOT_ALLOWED");
  }

  return data;
}

export async function waitForBookAssemblyJobCompletion(input: {
  intervalMs?: number;
  jobId: string;
  timeoutMs?: number;
}) {
  const intervalMs = input.intervalMs ?? 1_500;
  const timeoutMs = input.timeoutMs ?? 90_000;
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const job = await loadBookAssemblyJob(input.jobId);

    if (job.status === "complete") {
      return job;
    }

    if (job.status === "failed") {
      throw await normalizeAssemblyError(
        job.failureCode ?? job.failureMessage ?? "BOOK_EXPORT_PROCESSING_FAILED",
      );
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new BookAssemblyActionError("processing-failed", "BOOK_EXPORT_PROCESSING_FAILED");
}

export async function assembleBook(input: {
  draft: Parameters<typeof buildStartBookExportPayload>[1];
  source: Parameters<typeof buildStartBookExportPayload>[0];
}) {
  const started = await startBookExport(input);
  const job = await waitForBookAssemblyJobCompletion({
    jobId: started.jobId,
  });
  const primaryArtifact = getPrimaryBookExportArtifact(job.artifacts);

  if (!primaryArtifact) {
    throw new BookAssemblyActionError("generation-failed", "BOOK_EXPORT_PROCESSING_FAILED");
  }

  const signedDownload = await createSignedBookExportUrl(primaryArtifact.storagePath);

  return {
    assemblyId: job.id,
    downloadUrl: signedDownload.signedUrl,
    expiresInSeconds: signedDownload.expiresInSeconds,
    fileName: primaryArtifact.fileName,
    generatedAt: job.completedAt ?? new Date().toISOString(),
    jobId: job.id,
    target: primaryArtifact.target,
  } satisfies BookAssemblyResult;
}
